<script setup lang="ts">
  import { toTypedSchema } from '@vee-validate/zod';
  import { useForm } from 'vee-validate';
  import { computed, onBeforeMount, ref } from 'vue';
  import * as zod from 'zod';

  import type { DisputeReason } from '@stuut/api/models';
  import { Button } from '@ui/components/button';
  import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@ui/components/form';
  import { Input } from '@ui/components/input';
  import { Label } from '@ui/components/label';
  import { toast } from '@ui/components/sonner';
  import { Switch } from '@ui/components/switch';
  import { Textarea } from '@ui/components/textarea';
  import { isEmailValid } from '@ui/utils/utils';

  import { stuut } from '@/api';
  import ContactTagsList from '@/components/common/ContactTagsList.vue';
  import { useFeatureFlag } from '@/components/feature-flags';
  import { useProfile, useUsers } from '@/store';

  const props = defineProps<Props>();

  const emits = defineEmits<{
    (e: 'complete', DisputeReason): void;
    (e: 'close'): void;
  }>();

  const profileStore = useProfile();
  const userStore = useUsers();
  const users = computed(() => userStore.users);

  // Auto-activate / auto-routing require the Disputes Pro tier and the
  // dispute-automation feature flag.
  const isDisputesPro = computed(() => profileStore.isDisputesEnabled);
  const { value: isAutomationFlagEnabled } = useFeatureFlag('ff-dispute-automation');
  const showAutomation = computed(
    () => isDisputesPro.value && Boolean(isAutomationFlagEnabled.value),
  );

  interface Props {
    disputeReason?: DisputeReason;
  }

  const submitting = ref(false);

  const autoActivate = ref(props.disputeReason?.auto_dispute_activate ?? false);
  const automaticRouting = ref(props.disputeReason?.auto_dispute_routing ?? false);
  const routingEmails = ref<string[]>(props.disputeReason?.auto_routing_emails ?? []);

  const formData = useForm({
    initialValues: {
      code: props.disputeReason?.code,
      description: props.disputeReason?.description,
      name: props.disputeReason?.name,
      auto_routing_emails: routingEmails.value,
    },
    validationSchema: toTypedSchema(
      zod
        .object({
          code: zod.string().optional().nullable(),
          description: zod.string().optional().nullable(),
          name: zod.string().optional().nullable(),
          auto_routing_emails: zod.array(zod.string()).optional().nullable(),
        })
        .superRefine((data, ctx) => {
          if (!data.code) {
            ctx.addIssue({
              code: zod.ZodIssueCode.custom,
              path: ['code'],
              message: 'Code is required.',
            });
          }

          if (!data.description) {
            ctx.addIssue({
              code: zod.ZodIssueCode.custom,
              path: ['description'],
              message: 'Description is required.',
            });
          }

          if (!(showAutomation.value && automaticRouting.value)) return;

          const emails = (data.auto_routing_emails ?? [])
            .map((email) => email.trim())
            .filter(Boolean);

          if (!emails.length || emails.some((email) => !isEmailValid(email))) {
            ctx.addIssue({
              code: zod.ZodIssueCode.custom,
              path: ['auto_routing_emails'],
              message: 'Add at least one valid routing email.',
            });
          }
        }),
    ),
  });

  const onEmailListUpdate = (emails: string[]) => {
    routingEmails.value = emails;
    formData.setFieldValue('auto_routing_emails', emails);
  };

  const onSubmit = async (e: Event) => {
    e.preventDefault();

    if (submitting.value) return;

    submitting.value = true;

    const result = await formData.validate();

    if (!result.valid) {
      submitting.value = false;

      return;
    }

    try {
      // Routing settings are pro-tier only; the API rejects these keys for non-pro orgs.
      const routingData = showAutomation.value
        ? {
            auto_dispute_activate: autoActivate.value,
            auto_dispute_routing: automaticRouting.value,
            auto_routing_emails: automaticRouting.value ? routingEmails.value : [],
          }
        : {};

      if (props.disputeReason?.id) {
        const data = {
          description: result.values?.description,
          name: result.values?.name,
          ...routingData,
        };

        const response = await stuut.disputeReasons.update(
          props.disputeReason?.id,
          { data },
          props.disputeReason,
        );

        emits('complete', response);
        toast.success('Dispute reason updated');
      } else {
        const data = {
          code: result.values?.code,
          description: result.values?.description,
          name: result.values?.name,
          ...routingData,
        };

        const response = await stuut.disputeReasons.create(data);
        emits('complete', response);
        toast.success('Dispute reason created');
      }

      emits('close');
    } catch (err: any) {
      toast.error(err.clientMessage);
    } finally {
      submitting.value = false;
    }
  };

  onBeforeMount(() => userStore.fetchUsers());
</script>

<template>
  <form class="space-y-4 mr-1.5" @submit="onSubmit">
    <FormField v-slot="{ componentField }" name="name">
      <FormItem>
        <FormLabel class="text-xs"> Name </FormLabel>

        <FormControl>
          <Input
            type="text"
            v-bind="componentField"
            :disabled="submitting"
            placeholder="Name (optional)"
          />
        </FormControl>
      </FormItem>
    </FormField>

    <FormField v-slot="{ componentField }" name="code">
      <FormItem>
        <FormLabel class="text-xs" :class="disputeReason?.id && 'opacity-60'"> Code </FormLabel>

        <FormControl>
          <Input
            type="text"
            v-bind="componentField"
            :disabled="submitting || disputeReason?.id"
            placeholder="Code"
          />
        </FormControl>

        <FormMessage class="text-xs" />
      </FormItem>
    </FormField>

    <FormField name="description">
      <FormItem>
        <FormLabel class="text-xs"> Description </FormLabel>

        <FormControl>
          <Textarea
            :model-value="formData.values.description"
            class="min-w-full min-h-40 h-full"
            placeholder="Description"
            @update:model-value="(v) => formData.setFieldValue('description', v as string)"
          />
        </FormControl>

        <FormMessage class="text-xs" />
      </FormItem>
    </FormField>

    <template v-if="showAutomation">
      <div class="flex items-center justify-between gap-8">
        <div class="flex flex-col gap-y-1">
          <Label class="text-xs"> Auto activate </Label>

          <p class="text-xs text-muted-foreground">
            Let the agent auto-activate new dispute cases. If off, they stay in draft until manual
            activation.
          </p>
        </div>

        <Switch v-model="autoActivate" :disabled="submitting" />
      </div>

      <div class="flex items-center justify-between gap-8">
        <div class="flex flex-col gap-y-1">
          <Label class="text-xs"> Automatic routing </Label>

          <p class="text-xs text-muted-foreground">
            Automatically share a snapshot of the dispute case with the below addresses on creation.
          </p>
        </div>

        <Switch v-model="automaticRouting" :disabled="submitting" />
      </div>

      <FormField v-if="automaticRouting" v-slot="{ errorMessage }" name="auto_routing_emails">
        <FormItem class="pb-4">
          <FormControl>
            <ContactTagsList
              :invalid="!!errorMessage"
              :model-value="routingEmails"
              :users="users"
              @update:model-value="onEmailListUpdate"
            />
          </FormControl>

          <FormMessage class="text-xs" />
        </FormItem>
      </FormField>
    </template>

    <div class="flex gap-x-4 sticky bottom-0">
      <Button
        class="flex-1"
        type="button"
        variant="outline"
        :disabled="submitting"
        @click="emits('close')"
      >
        Cancel
      </Button>

      <Button class="flex-1" type="submit" :loading="submitting">
        {{ disputeReason?.id ? 'Update' : 'Create' }}
      </Button>
    </div>
  </form>
</template>

<style scoped></style>
